import { useEffect, useState, type FormEvent } from 'react';
import { Button, Field, Input, Modal, useErrorMessage, useFormErrors } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { ownerActionReset, replaceOwnerInvitation } from '../store/organizations.slice';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  UnknownOutcome,
  isOutcomeUnknown,
  fieldErrorPaths,
  looksLikeEmail,
  useStaffName,
} from './dialog-parts';
import { OwnerEmailConfirm, OwnerEmailField, useOwnerEmailGate } from './owner-email-check';
import styles from './organizations.module.css';

/**
 * SAHİP DAVETİNİ BAŞKA ADRESE GÖNDER / YENİ SAHİP DAVETİ (P5 sahip eylemleri).
 * `revokeFirst` iken bekleyen davet önce iptal edilir; eski bağlantı ölür.
 * Başarıda diyalog kapanır, sahip bloğu sunucudan tazelenir.
 *
 * TP1: başlığın altında kurum adı, "başka adrese" hâlinde mevcut davet adresi
 * salt okunur alanda; yeni adres kişisel bir hesaptaysa alan uyarır ve
 * gönderim ikinci onay ister (KH8 · Story H7).
 */
export function OwnerInvitationDialog({
  organizationId,
  organizationName,
  currentEmail,
  revokeFirst,
  onClose,
}: {
  organizationId: string;
  organizationName: string;
  currentEmail: string | null;
  revokeFirst: boolean;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const errorMessage = useErrorMessage();
  const staffName = useStaffName();
  const action = useAppSelector((root: RootState) => root.panelOrgs.ownerAction);
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  /*
   * Diyalog yalnız KENDİ gönderiminin sonucuyla kapanır. Önceki davetin başarısı
   * kartın notu için depoda kalır; açılıştaki sıfırlama aynı commit'teki kapanış
   * etkisinden SONRA görünür, bu bayrak olmadan başarılı davetten sonra yeniden
   * açılan diyalog ilk karede kendini kapatıyordu (`remove-cover-dialog` emsali).
   */
  const [sent, setSent] = useState(false);

  useEffect(() => {
    dispatch(ownerActionReset());
  }, [dispatch]);

  const replacing = action.kind === 'replace';
  const busy = replacing && action.phase === 'loading';
  useEffect(() => {
    if (sent && replacing && action.phase === 'succeeded') onClose();
  }, [action.phase, onClose, replacing, sent]);

  const serverFieldError = replacing && fieldErrorPaths(action.error).has('ownerEmail');
  const serverForm = useFormErrors(replacing ? action.error : null, ['ownerEmail']);
  const clientError = touched
    ? email.trim() === ''
      ? t.t('admin.orgs.createDialog.emailRequired')
      : !looksLikeEmail(email)
        ? t.t('admin.orgs.createDialog.emailInvalid')
        : undefined
    : undefined;
  const fieldError =
    clientError ??
    (serverFieldError
      ? (serverForm.at('ownerEmail') ?? t.t('admin.orgs.createDialog.emailInvalid'))
      : undefined);
  const failed = replacing && action.phase === 'failed' && !serverFieldError;
  // Yanıt gelmediyse iptal ya da davet sunucuda yapılmış olabilir: kesin cümle yok.
  const unknown = failed && isOutcomeUnknown(action.error);

  const send = () => {
    setSent(true);
    dispatch(
      replaceOwnerInvitation.request({ organizationId, ownerEmail: email.trim(), revokeFirst }),
    );
  };
  const gate = useOwnerEmailGate({ email, send, inputId: 'owner-email' });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTouched(true);
    if (email.trim() === '' || !looksLikeEmail(email)) return;
    gate.request();
  };

  if (gate.confirming) {
    return (
      <OwnerEmailConfirm
        email={email.trim()}
        staffName={staffName}
        onSendAnyway={gate.sendAnyway}
        onChange={gate.changeAddress}
      />
    );
  }

  return (
    <Modal
      open
      title={
        revokeFirst ? t.t('admin.orgs.detail.replaceTitle') : t.t('admin.orgs.detail.inviteTitle')
      }
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.t('admin.orgs.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="owner-invitation-form"
            loading={busy}
            loadingLabel={t.t('admin.orgs.detail.sending')}
          >
            {failed ? t.t('admin.orgs.retry') : t.t('admin.orgs.detail.sendInvite')}
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub>{organizationName}</DialogSub>
      <form id="owner-invitation-form" className={styles.form} onSubmit={submit} noValidate>
        {revokeFirst && currentEmail ? (
          <Field
            id="owner-current-email"
            label={t.t('admin.orgs.detail.currentEmail')}
            hint={t.t('admin.orgs.detail.currentEmailNote')}
          >
            <Input
              id="owner-current-email"
              className={[styles.mono, styles.readonlyInput].join(' ')}
              value={currentEmail}
              readOnly
            />
          </Field>
        ) : null}
        <OwnerEmailField
          id="owner-email"
          value={email}
          status={gate.status}
          error={fieldError}
          disabled={busy}
          placeholder={t.t('admin.orgs.createDialog.ownerEmailPlaceholder')}
          onChange={setEmail}
          onBlur={gate.check}
          onRetry={gate.check}
        />
        {revokeFirst ? null : (
          <InfoBlock tone="info">{t.t('admin.orgs.detail.inviteInfo')}</InfoBlock>
        )}
        {unknown ? (
          <UnknownOutcome reloaded={t.t('admin.write.reloadedOwner')} />
        ) : failed ? (
          <ServerBanner>
            {errorMessage(action.error) ?? t.t('admin.orgs.detail.actionError')}
          </ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}
